/**
 * Scoring-first matchmaking — flag helper and scoring adjustments.
 * When enabled, fairness/wait/variety are scoring-only (no structural guarantees).
 */

import { MATCHMAKING_CONFIG, debugLog } from './matchmakingConfig.js';
import {
  computeQuartetNovelty,
  computeSessionDebtFairnessBoost,
  computeRecentLoadDebt,
  computeComboVarietyDebt,
  getSessionDebtConfig,
} from './sessionDebt.js';
import { computeSessionSaturationScore } from './sessionVarietyMetrics.js';

/**
 * @returns {boolean}
 */
export function isScoringFirstMatchmakingEnabled() {
  return MATCHMAKING_CONFIG.featureFlags?.scoringFirstMatchmaking === true;
}

/**
 * @param {Object} sessionState
 * @param {string[]} eligiblePlayerIds
 * @param {Array} partnerships
 * @returns {Object}
 */
export function computeScoringFirstSaturation(sessionState, eligiblePlayerIds, partnerships = []) {
  const cfg = MATCHMAKING_CONFIG.scoringFirst || {};
  return computeSessionSaturationScore(sessionState, eligiblePlayerIds, partnerships, {
    pairSaturationWeight: cfg.pairSaturationWeight ?? 0.6,
    repeatQuartetWeight: cfg.repeatQuartetWeight ?? 0.4,
  });
}

/**
 * @param {number} finalScore
 * @param {string[]} team1
 * @param {string[]} team2
 * @param {Object[]} team1Objects
 * @param {Object[]} team2Objects
 * @param {Object} sessionState
 * @param {Object} playersById
 * @param {number|null} gamesMin
 * @param {Array} partnerships
 * @returns {number}
 */
export function applyScoringFirstComboAdjustments(
  finalScore,
  team1,
  team2,
  team1Objects,
  team2Objects,
  sessionState,
  playersById,
  gamesMin,
  partnerships = [],
) {
  if (finalScore === -Infinity || !isScoringFirstMatchmakingEnabled()) {
    return finalScore;
  }

  const cfg = MATCHMAKING_CONFIG.scoringFirst || {};
  let score = finalScore;

  const debtBoost = computeSessionDebtFairnessBoost(
    team1Objects,
    team2Objects,
    sessionState,
    playersById,
    gamesMin,
  );
  score += debtBoost * (cfg.waitUrgencyWeight ?? 0.35);

  const varietyDebt = computeComboVarietyDebt(
    team1Objects,
    team2Objects,
    sessionState,
    partnerships,
  );
  score += (varietyDebt - 50) * (cfg.varietyDebtWeight ?? 0.25);

  const comboIds = [...team1, ...team2];
  const quartetNovelty = computeQuartetNovelty(comboIds, sessionState);
  const saturation = sessionState?.varietySaturation;
  const saturated =
    (saturation?.repeatQuartetRate ?? 0) >= (cfg.saturatedRepeatQuartetRate ?? 0.2)
    || (saturation?.score ?? 0) >= (cfg.saturatedSessionScore ?? 0.25);

  if (quartetNovelty === 0) {
    score -= saturated
      ? (cfg.exactRepeatPenaltySaturated ?? 45)
      : (cfg.exactRepeatPenalty ?? 15);
    debugLog('🔁 Scoring-first: exact repeat quartet penalty', { saturated, quartetNovelty });
  } else if (saturated) {
    score += cfg.novelQuartetBoostSaturated ?? 30;
    debugLog('✨ Scoring-first: novel quartet boost', { saturated });
  }

  const debtConfig = getSessionDebtConfig();
  let recentLoadPenalty = 0;
  for (const player of [...team1Objects, ...team2Objects]) {
    recentLoadPenalty += computeRecentLoadDebt(
      player.id,
      sessionState,
      debtConfig.recentWindowGames,
    ) * debtConfig.weights.recent * (cfg.recentLoadMultiplier ?? 10);
  }
  if (recentLoadPenalty > 0) {
    score -= recentLoadPenalty;
  }

  return score;
}
